import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Search, X } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import MasterModal from "./MasterModal";
import MasterTable from "./MasterTable";
import { getCities, createCity, updateCity, toggleCityActive } from "@/services/masters";
import AddCityForm, { CityFormData } from "./cities/AddCity";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import MasterTableHeader from "./generics/MasterTableHeader";

interface City {
  id: string | number;
  name: string;
  state: string;
  isActive: boolean;
}

export default function Cities() {
  const { canCreate, canUpdate } = useScreenPermission("cities");
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [togglingId, setTogglingId] = useState<string | number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const methods = useForm<CityFormData>({
    defaultValues: { City_Name: "", State: "" }
  });

  const loadCities = useCallback(async (p: number = 1, search: string = "", status: string = statusFilter) => {
    setIsLoading(true);
    try {
      const response = await getCities(p, search, status);
      if (response.status === 200 && response.data) {
        const d = response.data as any;
        const items: any[] = d.results ?? (Array.isArray(d) ? d : []);
        setCities(items.map((item: any) => ({
          id: item.id!,
          name: item.City_Name,
          state: item.State,
          isActive: item.Is_Active ?? true,
        })));
        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count ?? 0);
      } else {
        // toast.error("Failed to load cities");
      }
    } catch (error) {
      toast.error("Error loading cities");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load + debounced search
  useEffect(() => {
    const delay = searchQuery ? 400 : 0;
    const timer = setTimeout(() => {
      setPage(1);
      loadCities(1, searchQuery, statusFilter);
    }, delay);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, loadCities]);

  const handleOpenModal = useCallback((city?: City) => {
    if (city) {
      const foundCity = cities.find(c => c.id === city.id);
      if (foundCity) {
        methods.reset({ City_Name: foundCity.name, State: foundCity.state });
        setEditingCity(foundCity);
      }
    } else {
      methods.reset({ City_Name: "", State: "" });
      setEditingCity(null);
    }
    setIsModalOpen(true);
  }, [cities, methods]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingCity(null);
    methods.reset();
  }, [methods]);

  const handleSubmit = useCallback(async (data: CityFormData) => {
    methods.clearErrors();
    setIsLoading(true);
    try {
      const response = editingCity?.id
        ? await updateCity(Number(editingCity.id), data)
        : await createCity(data);

      if (response.status === 201 || response.status === 200) {
        toast.success(editingCity ? "City updated successfully" : "City created successfully");
        handleCloseModal();
        await loadCities(page, searchQuery, statusFilter);
      } else if (response.errors && typeof response.errors === "object") {
        let hasFieldErrors = false;
        Object.entries(response.errors).forEach(([key, value]) => {
          const errorText = Array.isArray(value) ? value[0] : String(value);
          if (key === "error" || key === "non_field_errors" || key === "detail") {
            toast.error(errorText);
          } else {
            methods.setError(key as keyof CityFormData, { type: "server", message: errorText });
            hasFieldErrors = true;
          }
        });
        if (!hasFieldErrors) toast.error("Failed to save city");
      } else {
        toast.error("Failed to save city");
      }
    } catch (error) {
      toast.error("Error saving city");
    } finally {
      setIsLoading(false);
    }
  }, [editingCity, handleCloseModal, loadCities, methods, page, searchQuery]);

  const handleToggleActive = useCallback(async (city: City) => {
    if (!city.id) return;
    setTogglingId(city.id);
    try {
      const response = await toggleCityActive(Number(city.id), { Is_Active: !city.isActive });
      if (response.status === 200) {
        toast.success(`City ${!city.isActive ? "activated" : "deactivated"} successfully`);
        await loadCities(page, searchQuery, statusFilter);
      } else {
        toast.error("Failed to update city status");
      }
    } catch (error) {
      toast.error("Error updating city status");
    } finally {
      setTogglingId(null);
    }
  }, [loadCities, page, searchQuery, statusFilter]);

  const columns = useMemo(() => [
    { key: "name" as const, label: "District Name" },
    { key: "state" as const, label: "State" },
  ], []);

  return (
    <div className="space-y-6">
      <MasterTableHeader
        title="Districts Management"
        description="Create, edit, and manage districts"
        canCreate={canCreate}
        addButtonText="Add New District"
        onAdd={() => handleOpenModal()}
        searchPlaceholder="Search by district or state..."
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showDropdownFilter
        dropdownValue={statusFilter}
        onDropdownChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        showClearFilters
        onClearFilters={() => {
          setSearchQuery("");
          setStatusFilter("");
          setPage(1);

          loadCities(1, "", "");
        }}
      />

      <MasterTable
        columns={columns}
        data={cities}
        onEdit={handleOpenModal}
        onToggleActive={handleToggleActive}
        togglingId={togglingId}
        isLoading={isLoading && cities.length === 0}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => { setPage(p); loadCities(p, searchQuery, statusFilter); }}
        canEdit={canUpdate}
        canToggle={canUpdate}
        totalCount={totalCount}
      />

      <MasterModal
        isOpen={isModalOpen}
        title={editingCity ? "Edit District" : "Add New District"}
        onClose={handleCloseModal}
        onSubmit={methods.handleSubmit(handleSubmit)}
        isLoading={isLoading}
      >
        <FormProvider {...methods}>
          <AddCityForm />
        </FormProvider>
      </MasterModal>
    </div>
  );
}
